/** The color answer before the evidence: four useful roles first, then the
 * complete system, alternatives, source proof, and expert controls. */

import type { DirectionCard, PaletteRoleId, PaletteSystem, PreviewCopy } from "@core/index.js";

import { selectWorkingPalette, useStudio } from "../app/store";
import { SlidePreview, slidePaletteFromSystem } from "../components/SlidePreview";
import { EvidenceDrawer } from "./EvidenceDrawer";
import { SourceMap } from "./SourceMap";

const ROLE_ORDER: PaletteRoleId[] = ["background", "surface", "text", "muted_text", "accent_primary", "accent_secondary", "on_accent", "line_or_rule"];
const ESSENTIAL_ROLES: PaletteRoleId[] = ["background", "text", "accent_primary", "on_accent"];
const ROLE_LABELS: Record<PaletteRoleId, string> = {
  background: "Background",
  surface: "Panel",
  text: "Body text",
  muted_text: "Secondary text",
  accent_primary: "Accent",
  accent_secondary: "Second accent",
  on_accent: "Text on accent",
  line_or_rule: "Rules and dividers",
};

const wordsFor = (previewCopy?: PreviewCopy) => ({
  title: previewCopy?.slideTitle ?? previewCopy?.deckTitle ?? "Make the next decision easier.",
  body: previewCopy?.body ?? "Put the recommendation where the room can see it. Keep the evidence close enough to trust, then move the furniture into the appendix.",
  kicker: previewCopy?.deckTitle && previewCopy.deckTitle !== previewCopy.slideTitle ? previewCopy.deckTitle : "YOUR DECK",
});

const ColorResult = ({ palette }: { palette: PaletteSystem }) => {
  const supportingRoles = ROLE_ORDER.filter((id) => !ESSENTIAL_ROLES.includes(id));
  const renderRole = (id: PaletteRoleId) => {
    const role = palette.roles[id];
    if (!role) return null;
    return <li key={id}><span style={{ background: role.hex }} aria-hidden="true" /><strong>{ROLE_LABELS[id]}</strong><code>{role.hex}</code></li>;
  };
  return (
    <section className="result-panel result-panel--color" aria-labelledby="color-result-title">
      <p className="eyebrow">Your essential colors</p>
      <h3 id="color-result-title">{palette.name}</h3>
      <p className="result-reason">Four colors you can use immediately. The complete working system waits underneath when you need it.</p>
      <ul className="palette-strip palette-strip--essential" aria-label="Essential palette roles">{ESSENTIAL_ROLES.map(renderRole)}</ul>
      <details className="disclosure palette-complete">
        <summary>Open the complete palette</summary>
        <div className="disclosure-body"><ul className="palette-strip" aria-label="Supporting palette roles">{supportingRoles.map(renderRole)}</ul></div>
      </details>
    </section>
  );
};

export const DirectionCardView = ({ direction, onOpenLab, onOpenBench, onOpenExport }: {
  direction: DirectionCard;
  onOpenLab: () => void;
  onOpenBench: () => void;
  onOpenExport: () => void;
  onAddColour?: () => void;
  onAddType?: () => void;
}) => {
  const { state, dispatch } = useStudio();
  const palette = selectWorkingPalette(state) ?? direction.colour?.systems.find((system) => system.id === direction.selected.paletteSystemId) ?? null;
  const allPalettes = state.colourRun ? [...state.colourRun.systems, ...state.colourRun.companionSystems] : [];
  const words = wordsFor(state.brief.previewCopy);
  const isRehearsal = state.source?.origin === "rehearsal";

  if (!palette) {
    return (
      <div className="result-boundary">
        <p className="eyebrow">No palette yet</p>
        <h2 className="step-title">The image needs another look.</h2>
        <p className="step-intro">We did not get a usable color system. Your image and answers are still here.</p>
        <button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "setStep", step: "source" })}>Back to my image</button>
      </div>
    );
  }

  return (
    <div className="direction-result">
      <header className="result-head" data-rehearsal={isRehearsal ? "" : undefined}>
        <p className="eyebrow">{isRehearsal ? "Rehearsal result · built-in light study" : "Made from your image"}</p>
        <h2 className="step-title">{palette.name}.</h2>
        <p className="step-intro">{isRehearsal
          ? "This proves the tool, not your deck. Play with everything—then bring your own image before you take the palette seriously."
          : "A working color world drawn from your image. Four essential colors are ready now; the complete system and expert lab wait one layer deeper."}</p>
      </header>

      <div className="result-grid"><ColorResult palette={palette} /></div>

      <div className="result-slide-stage">
        <div className="result-slide-aura" aria-hidden="true"><span /><span /><span /></div>
        <SlidePreview
          title={words.title}
          body={words.body}
          kicker={words.kicker}
          palette={slidePaletteFromSystem(palette)}
          variant="titleBody"
          label="A real 16:9 slide · your color direction"
        />
      </div>

      {allPalettes.length > 1 ? (
        <details className="disclosure alternatives">
          <summary>Compare other color directions</summary>
          <div className="disclosure-body alternatives-grid">
            <section>
              <h3>Six viable worlds</h3>
              <p>Three strategies, each built separately for light and dark. Pick the one that belongs to the deck—not the loudest swatch in the room.</p>
              {allPalettes.map((item) => (
                <button type="button" className="alternative-row" key={item.id} aria-pressed={item.id === state.selectedPaletteId} onClick={() => dispatch({ type: "selectPalette", systemId: item.id })}>
                  <span className="alternative-swatches" aria-hidden="true">{[item.roles.background, item.roles.text, item.roles.accent_primary, item.roles.accent_secondary].filter(Boolean).map((token) => <i key={token!.hex} style={{ background: token!.hex }} />)}</span>
                  <strong>{item.name}</strong>
                  <span>{item.strategy.replaceAll("_", " ")} · {item.mode}</span>
                </button>
              ))}
            </section>
          </div>
        </details>
      ) : null}

      <div className="result-actions">
        <button type="button" className="btn btn--primary" onClick={onOpenBench}>Play with the colors</button>
        <button type="button" className="btn" onClick={onOpenLab}>Try five real slides</button>
        <button type="button" className="btn" onClick={onOpenExport}>Take the palette away</button>
      </div>

      <details className="disclosure technical-details">
        <summary>Why this answer—and what could break it</summary>
        <div className="disclosure-body technical-stack">
          <section>
            <h3>Watch these pairs</h3>
            {direction.colour?.recommendation.weaknesses.length
              ? <ul>{direction.colour.recommendation.weaknesses.map((item) => <li key={item.code}>{item.summary}</li>)}</ul>
              : <p>No blocking weakness in the selected system. Still test the finished slide in the room that matters.</p>}
          </section>
          {state.source?.analysis ? <SourceMap source={state.source} /> : null}
          <EvidenceDrawer direction={direction} />
        </div>
      </details>
    </div>
  );
};
